import React, { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { howItWorksSteps } from '../mock';
import { ArrowRight } from 'lucide-react';

export const HowItWorks = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: false, amount: 0.1 });
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  return (
    <section
      id="how-it-works"
      ref={containerRef}
      className="py-24 relative overflow-hidden"
      style={{ backgroundColor: '#000' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            How It Works
          </h2>
          <p className="text-xl max-w-3xl mx-auto" style={{ color: '#bbb5ae' }}>
            Get started in minutes with our simple four-step process
          </p>
        </motion.div>

        {/* Steps */}
        <div className="space-y-32">
          {howItWorksSteps.map((step, index) => {
            const stepRef = useRef(null);
            const stepInView = useInView(stepRef, { once: false, amount: 0.5 });
            const isEven = index % 2 === 0;

            return (
              <motion.div
                key={step.step}
                ref={stepRef}
                initial={{ opacity: 0 }}
                animate={{ opacity: stepInView ? 1 : 0 }}
                transition={{ duration: 0.8 }}
                className={`flex flex-col ${
                  isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } items-center gap-12`}
              >
                {/* Image */}
                <motion.div
                  initial={{ opacity: 0, x: isEven ? -50 : 50 }}
                  animate={{
                    opacity: stepInView ? 1 : 0,
                    x: stepInView ? 0 : isEven ? -50 : 50,
                  }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="flex-1 w-full"
                >
                  <div className="relative group">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-96 object-cover rounded-2xl transition-transform duration-500 group-hover:scale-105"
                      style={{
                        boxShadow: '0 20px 40px rgba(255, 255, 255, 0.3)',
                      }}
                    />
                    {/* Overlay gradient on hover */}
                    <div
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{
                        background:
                          'linear-gradient(135deg, rgba(97, 82, 90, 0.3) 0%, rgba(40, 55, 80, 0.3) 100%)',
                      }}
                    />
                  </div>
                </motion.div>

                {/* Content */}
                <motion.div
                  initial={{ opacity: 0, x: isEven ? 50 : -50 }}
                  animate={{
                    opacity: stepInView ? 1 : 0,
                    x: stepInView ? 0 : isEven ? 50 : -50,
                  }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="flex-1"
                >
                  <div
                    className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                    style={{ backgroundColor: '#fff' }}
                  >
                    <span className="text-3xl font-bold text-black">
                      {step.step}
                    </span>
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                    {step.title}
                  </h3>
                  <p
                    className="text-lg leading-relaxed mb-6"
                    style={{ color: '#bbb5ae' }}
                  >
                    {step.description}
                  </p>
                  <motion.button
                    whileHover={{ x: 5 }}
                    className="inline-flex items-center space-x-2 text-white font-medium group"
                  >
                    <span>Learn more</span>
                    <ArrowRight
                      size={20}
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </motion.button>
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
